import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { getDocument } from '@atlaskit/browser-apis';

import Modal from '@atlaskit/modal-dialog/modal-dialog';

import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import useSpotlight from '@atlaskit/onboarding/use-spotlight';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TwcOnboardingModalContent } from './modal/TwcOnboardingModalContent';

import SpotlightTour, { type SpotlightTourProps } from './spotlight/SpotlightTour';

import { SpotlightTourMode, type Variant } from './types';

import { SPOTLIGHT_DISMISS_EVENT } from '../index';

import { FOR_YOU_TARGET } from './spotlight/constants';

import { type JSX } from 'react/jsx-runtime';

export type OnboardingProps = {
	dismiss: () => void;
	spotlightTourMode: SpotlightTourMode;
	variant: Exclude<Variant, 'none'>;
	appOneName?: string;
	appTwoName?: string;
} & Omit<SpotlightTourProps, 'dismiss' | 'sidebarForcedOpen'>;

export const Onboarding = (props: OnboardingProps): JSX.Element => {
	const {
		dismiss,
		variant,
		spotlightTourMode,
		userName,
		isRovoChatOpen,
		openRovoPanel,
		toggleSidebar,
		toggleSwitcher,
		appOneName,
		appTwoName,
	} = props;
	const [tourInProgress, setTourInProgress] = useState(false);
	const hasDismissed = useRef(false);
	const [sidebarForcedOpen, setSidebarForcedOpen] = useState(false);
	const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { onClosed, onAcknowledged, onCtaActioned } = usePostOfficeUserInteractions();
	const { messageDismissed } = useMessageEvent();
	const { checkVisibility } = useSpotlight();
	const setTimeoutToUse = props._setTimeout || setTimeout;

	// TODO: Add checks all the function props are defined before trying to render spotlight tour

	const handleDismiss = useCallback(
		(
			dismissedThroughBlanketClick: boolean,
			interaction: 'closed' | 'acknowledged' | 'ctaActioned' | 'recorded' = 'closed',
		) => {
			if (fg('post-office_enable_semantic-event-fixes')) {
				// Modal and document Escape handlers can dismiss the same message.
				if (hasDismissed.current) {
					return;
				}
				hasDismissed.current = true;
			}
			// close sidebar if it was forced open && it's still open
			if (sidebarForcedOpen && checkVisibility(FOR_YOU_TARGET)()) {
				toggleSidebar?.();
			}

			// close switcher if it was open
			if (isSwitcherOpen) {
				toggleSwitcher?.('close');
			}

			// Mark message as dismissed in Post Office lifecycle
			void messageDismissed();

			dismiss();

			if (fg('post-office_enable_semantic-event-fixes')) {
				if (interaction === 'acknowledged') {
					onAcknowledged({ interactionIdentifier: 'townsquareTwcModalGotIt' });
				} else if (interaction === 'ctaActioned') {
					onCtaActioned({ interactionIdentifier: 'townsquareTwcModalExplore' });
				} else if (interaction === 'closed') {
					onClosed({
						interactionIdentifier: dismissedThroughBlanketClick
							? 'townsquareTwcModalClickAway'
							: 'townsquareTwcModalDismiss',
					});
				}
			} else if (!dismissedThroughBlanketClick) {
				void messageInstanceClicked({
					action: {
						actionType: 'dismiss',
					},
					clickedElement: {
						clickIdentifier: 'townsquareTwcModalDismiss',
						clickedElement: 'button',
						buttonType: 'primary',
					},
					messageEventType: 'clicked',
					additionalMetadataFromFrontend: {},
				});
			}
		},
		[
			dismiss,
			messageDismissed,
			messageInstanceClicked,
			onClosed,
			onAcknowledged,
			onCtaActioned,
			checkVisibility,
			isSwitcherOpen,
			sidebarForcedOpen,
			toggleSidebar,
			toggleSwitcher,
		],
	);

	const attributes = useMemo(
		() => ({
			variant,
			spotlightTourMode,
		}),
		[variant, spotlightTourMode],
	);

	const handleStartTour = useCallback(() => {
		setTimeoutToUse(() => {
			if (!checkVisibility(FOR_YOU_TARGET)()) {
				setSidebarForcedOpen(true);
				toggleSidebar?.();
			}
			// Wait for sidebar to open before moving on
			setTimeoutToUse(() => {
				setTourInProgress(true);
			});
		});
	}, [checkVisibility, toggleSidebar, setTimeoutToUse]);

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'viewed',
			actionSubject: 'twcModal',
			attributes,
		});
	}, [fireAnalyticsEvent, attributes]);

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				handleDismiss(true);
			}
		};

		const handleSpotlightDismiss = () => handleDismiss(true);
		const ownerDocument = getDocument();

		// both event listeners are needed to handle the dismiss of spotlight
		ownerDocument?.addEventListener('keydown', handleEscape);
		ownerDocument?.addEventListener(SPOTLIGHT_DISMISS_EVENT, handleSpotlightDismiss);

		return () => {
			ownerDocument?.removeEventListener('keydown', handleEscape);
			ownerDocument?.removeEventListener(SPOTLIGHT_DISMISS_EVENT, handleSpotlightDismiss);
		};
	}, [handleDismiss]);

	return (
		<>
			<ModalTransition>
				{!tourInProgress && (
					<Modal
						shouldCloseOnOverlayClick
						shouldCloseOnEscapePress
						width="968px"
						onClose={() => handleDismiss(true)}
						testId="twc-onboarding-modal"
					>
						<TwcOnboardingModalContent
							onDismiss={(interaction) => handleDismiss(false, interaction)}
							variant={variant}
							appOneName={appOneName}
							appTwoName={appTwoName}
							showSpotlightTour={spotlightTourMode !== SpotlightTourMode.DISABLED}
							onStartTour={handleStartTour}
						/>
					</Modal>
				)}
			</ModalTransition>
			{tourInProgress && (
				<SpotlightTour
					sidebarForcedOpen={sidebarForcedOpen}
					userName={userName}
					isRovoChatOpen={isRovoChatOpen}
					openRovoPanel={openRovoPanel}
					toggleSidebar={toggleSidebar}
					toggleSwitcher={(toggle) => {
						setIsSwitcherOpen(toggle === 'open');
						toggleSwitcher?.(toggle);
					}}
					dismiss={() => handleDismiss(false, 'recorded')}
					spotlightTourMode={spotlightTourMode}
				/>
			)}
		</>
	);
};
